// src/pages/Learning/Calendar.jsx (adjust path only if your router uses src/pages/Calendar.jsx)

// Unified read-only statutory/financial reminders + weekly financial check-in.

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import BottomBar from "../../components/layout/BottomBar";
import Banner from "../../components/layout/Banner";

import WeeklyFinancialCheckIn from "../../components/check-ins/WeeklyFinancialCheckIn";

import { supabase } from "../../supabaseClient";

import { usePushAutoRegister } from "../../hooks/usePushAutoRegister";

import { getMyCalendarReminders, refreshMyFinancialActions } from "../../lib/actions/calendarSetup";

import {

  buildWeeklyCheckInHistory, canEditWeeklyCheckIn,

  formatWeeklyCheckInDate,

  getMyWeeklyFinancialCheckIns, getOrStartMyWeeklyCheckInTracking,

  getWeeklyCheckInDates, getWeeklyCheckInStatus,

} from "../../lib/check-ins/weeklyCheckIn";





const addDays = (iso, days) => {

  const d = new Date(`${iso}T12:00:00Z`);

  d.setUTCDate(d.getUTCDate() + days);

  return d.toISOString().slice(0, 10);

};

const mondayOf = (iso) => addDays(iso, -((new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7));

const dateOnly = (value) => {

  if (!value) return null;

  // An occurrence due_at is timestamptz; display its calendar day in SAST.

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("en-GB", {

    timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit",

  }).formatToParts(date);

  const get = (kind) => parts.find((part) => part.type === kind)?.value;

  return `${get("year")}-${get("month")}-${get("day")}`;

};

const saToday = () => {

  const parts = new Intl.DateTimeFormat("en-GB", {

    timeZone: "Africa/Johannesburg", year: "numeric", month: "2-digit", day: "2-digit",

  }).formatToParts(new Date());

  const get = (kind) => parts.find((part) => part.type === kind)?.value;

  return `${get("year")}-${get("month")}-${get("day")}`;

};

const formatMoney = (value) => Number(value || 0).toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });

const pillStyle = {

  completed: "bg-[#E8F8EE] text-[#247A52]", missed: "bg-[#FFE9EA] text-[#D2454C]",

  upcoming: "bg-[#EFEFEF] text-[#757575]", due: "bg-[#FFF0E6] text-[#D46A21]",

  overdue: "bg-[#FFF0E6] text-[#D46A21]",

};

const statusLabel = { completed: "Completed", missed: "Missed", upcoming: "Upcoming", due: "Due", overdue: "Overdue" };



function categoryFor(row) {

  const code = String(

    row.metadata?.definition_code ||

    row.definition_code ||

    row.code ||

    row.source_key ||

    ""

  ).toUpperCase();



  const category = String(

    row.category || ""

  ).toLowerCase();



  // CIPC actions

  if (

    code.includes("CIPC") ||

    category.includes("cipc")

  ) {

    return "CIPC";

  }



  // Employer actions

  if (

    code.includes("UIF") ||

    code.includes("PAYE") ||

    code.includes("EMP201") ||

    code.includes("SDL") ||

    code.includes("COIDA") ||

    category.includes("payroll") ||

    category.includes("employer")

  ) {

    return "Employer";

  }



  // Tax actions

  if (

    code.includes("TAX_") ||

    category === "tax"

  ) {

    return "Tax";

  }



  // Financial actions

  return "Financial";

}

const categoryStyle = {

  Tax: "bg-[#FFF0E6] text-[#B35416]",

  CIPC: "bg-[#ECEDFF] text-[#4A4F9C]",

  Employer: "bg-[#E9F5FF] text-[#246C9C]",

  Financial: "bg-[#E9F8EE] text-[#247A52]",

};

function makeGrid(month) {

  const year = month.getFullYear();

  const m = month.getMonth();

  const first = `${year}-${String(m + 1).padStart(2, "0")}-01`;

  const next = new Date(year, m + 1, 1);

  const last = addDays(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-01`, -1);

  const start = mondayOf(first);

  const end = addDays(mondayOf(last), 6);

  const cells = [];

  for (let iso = start; iso <= end; iso = addDays(iso, 1)) {

    cells.push({ iso, inMonth: iso.slice(0, 7) === first.slice(0, 7) });

  }

  return { start, end, cells };

}



export default function CalendarPage() {

  const navigate = useNavigate();

  usePushAutoRegister();

  const [month, setMonth] = useState(() => {

    const today = saToday();

    return new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, 1);

  });

  const [selectedISO, setSelectedISO] = useState(saToday);

  const [trackingStart, setTrackingStart] = useState(null);

  const [submissions, setSubmissions] = useState([]);

  const [reminders, setReminders] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [reminderWarning, setReminderWarning] = useState("");

  const [formWeek, setFormWeek] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const [filter, setFilter] = useState("All");

  const { start: gridStart, end: gridEnd, cells } = useMemo(() => makeGrid(month), [month]);



  const load = useCallback(async () => {

    setLoading(true);

    setError("");

    setReminderWarning("");

    try {

      const { data, error: authError } = await supabase.auth.getUser();

      if (authError) throw authError;

      if (!data?.user) { navigate("/login", { replace: true }); return; }

      const [weekStart, weekly] = await Promise.all([

        getOrStartMyWeeklyCheckInTracking(), getMyWeeklyFinancialCheckIns(),

      ]);

      setTrackingStart(weekStart);

      setSubmissions(weekly);

      try {

        await refreshMyFinancialActions();

      } catch (e) {

        // Do not hide other calendar data if one generator has not yet been deployed.

        setReminderWarning(`Some reminder dates could not be refreshed: ${e?.message || "Unknown error"}`);

      }

      try {

        setReminders(await getMyCalendarReminders());

      } catch (e) {

        setReminderWarning((old) => `${old ? `${old} ` : ""}Could not load calendar reminders: ${e?.message || "Unknown error"}`);

      }

    } catch (e) {

      setError(e?.message || "Could not load calendar.");

    } finally { setLoading(false); }

  }, [navigate]);



  useEffect(() => { void load(); }, [load, refreshKey]);



  const weeks = useMemo(() => {

    if (!trackingStart) return [];

    const now = new Date();

    const historic = buildWeeklyCheckInHistory(submissions, trackingStart, now);

    const byWeek = new Map(historic.map((w) => [w.week_start, w]));

    const result = [];

    for (let monday = mondayOf(gridStart); monday <= gridEnd; monday = addDays(monday, 7)) {

      if (monday < trackingStart) continue;

      const existing = byWeek.get(monday);

      const submission = existing?.submission || submissions.find((s) => s.week_start === monday) || null;

      const dates = getWeeklyCheckInDates(monday);

      result.push({ week_start: monday, ...dates,

        status: existing?.status || getWeeklyCheckInStatus(monday, submission, now), submission });

    }

    return result;

  }, [gridStart, gridEnd, submissions, trackingStart]);



  const checkinsByDate = useMemo(() => new Map(weeks.map((week) => [week.dueDate, week])), [weeks]);

  const remindersByDate = useMemo(() => {

    const map = new Map();

    for (const reminder of reminders) {

      const iso = dateOnly(reminder.due_at);

      if (!iso) continue;

      if (!map.has(iso)) map.set(iso, []);

      map.get(iso).push({ ...reminder, calendarCategory: categoryFor(reminder) });

    }

    return map;

  }, [reminders]);

  const dayReminders = (remindersByDate.get(selectedISO) || []).filter((row) => filter === "All" || row.calendarCategory === filter);

  const selectedWeek = checkinsByDate.get(selectedISO) || null;

  const label = month.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });

  const openCheckIn = (week) => {

    if (!week || week.submission || !canEditWeeklyCheckIn(week.week_start)) return;

    setFormWeek(week.week_start);

  };



  return (

    <div className="min-h-screen bg-[#F8F8F8] pb-24 text-[#222222]">

      <div className="bg-white">
        <Banner homeTo="/progress-dashboard" />
      </div>
      <header className="border-b border-[#EAEAEA] bg-white px-5 py-6">
        <div className="mx-auto max-w-3xl">
          <button type="button" onClick={() => navigate("/progress-dashboard")}
            className="mb-4 text-sm font-semibold text-[#757575] hover:text-[#FF914D]">
            ← Back
          </button>
          <h1 className="text-[26px] font-black tracking-tight text-[#111111]">Financial Calendar</h1>
          <p className="mt-2 text-[13px] leading-relaxed text-[#757575]">
            Your financial dates, tax and compliance reminders
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6">

        {error && <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error} <button onClick={() => void load()} className="font-bold underline">Retry</button></div>}

        {reminderWarning && <div className="rounded-xl bg-amber-50 p-4 text-xs text-amber-800">{reminderWarning}</div>}

        <section className="rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-sm">

          <div className="mb-5 flex items-center justify-between gap-2">

            <button type="button" aria-label="Previous month" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))} className="rounded-lg border px-3 py-2">←</button>

            <h2 className="text-lg font-black">{label}</h2>

            <button type="button" aria-label="Next month" onClick={() => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))} className="rounded-lg border px-3 py-2">→</button>

          </div>

          <div className="mb-3 flex flex-wrap gap-2">

            {["All", "Tax", "CIPC", "Employer", "Financial"].map((category) =>

              <button key={category} type="button" onClick={() => setFilter(category)} className={`rounded-full px-3 py-1 text-xs font-bold ${filter === category ? "bg-[#FF914D] text-white" : "bg-[#F2F2F2] text-[#777]"}`}>{category}</button>

            )}

          </div>

          <div className="mb-2 grid grid-cols-7 text-center text-[11px] font-bold text-[#757575]">

            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d}>{d}</span>)}

          </div>

          <div className="grid grid-cols-7 gap-1.5">

            {cells.map((cell) => {

              const week = checkinsByDate.get(cell.iso);

              const count = (remindersByDate.get(cell.iso) || []).filter((r) => filter === "All" || r.calendarCategory === filter).length;

              const selected = cell.iso === selectedISO;

              return <button key={cell.iso} type="button" onClick={() => setSelectedISO(cell.iso)} aria-label={`Select ${cell.iso}`}

                className={`min-h-[70px] rounded-xl border p-1.5 text-left ${selected ? "border-[#FF914D] ring-2 ring-[#FF914D]/40" : "border-[#EAEAEA]"} ${cell.inMonth ? "bg-white" : "bg-[#F6F6F6] text-[#AAAAAA]"}`}>

                <span className="text-xs font-bold">{Number(cell.iso.slice(-2))}</span>

                {count > 0 && <span className="mt-1 block rounded bg-[#FFF0E6] px-1 text-center text-[9px] font-bold text-[#A35220]">{count} reminder{count > 1 ? "s" : ""}</span>}

                {week && <span className={`mt-1 block rounded px-1 py-1 text-center text-[8px] font-bold ${pillStyle[week.status] || pillStyle.upcoming}`}>Check-in</span>}

              </button>;

            })}

          </div>

          <p className="mt-4 text-xs text-[#757575]">Select a date to see your personalised reminders. Regulatory dates are informational; always verify them with the relevant authority.</p>

        </section>

        <section className="rounded-2xl border border-[#E5E5E5] bg-white p-5 shadow-sm">

          <div className="mb-3 flex items-center justify-between gap-3">

            <h3 className="text-base font-black">{formatWeeklyCheckInDate(selectedISO)}</h3>

            <button type="button" onClick={() => setRefreshKey((n) => n + 1)} className="text-xs font-semibold text-[#FF914D]">Refresh</button>

          </div>

          {loading ? <p className="text-sm text-[#757575]">Loading calendar...</p> : <>

            {dayReminders.length ? <div className="space-y-3">

              {dayReminders.map((r) => <div key={r.id || r.source_key} className="rounded-xl border border-[#ECECEC] p-3">

                <div className="mb-2 flex items-center justify-between gap-2">

                  <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${categoryStyle[r.calendarCategory]}`}>{r.calendarCategory}</span>

                  <span className="text-[10px] font-semibold text-[#888]">{r.metadata?.deadline_is_official === false ? "Planning action" : "Calendar reminder"}</span>

                </div>

                <p className="text-sm font-bold">{r.title}</p>

                {r.description && <p className="mt-1 text-xs text-[#777]">{r.description}</p>}

                <p className="mt-2 text-xs text-[#888]">{r.metadata?.deadline_is_official === false ? "Review by" : "Date"}: {formatWeeklyCheckInDate(selectedISO)}</p>

              </div>)}

            </div> : <p className="text-sm text-[#757575]">No {filter === "All" ? "" : `${filter.toLowerCase()} `}reminders for this date.</p>}

            {selectedWeek && <div className="mt-5 space-y-3 rounded-xl border border-[#FFE0CE] p-4">

              <div className="flex items-center justify-between gap-3">

                <p className="text-sm font-bold">Weekly Financial Check-In</p>

                <span className={`rounded-full px-3 py-1 text-xs font-bold ${pillStyle[selectedWeek.status] || pillStyle.upcoming}`}>{statusLabel[selectedWeek.status] || selectedWeek.status}</span>

              </div>

              <p className="text-xs text-[#757575]">Reporting week: {formatWeeklyCheckInDate(selectedWeek.weekStart)} – {formatWeeklyCheckInDate(selectedWeek.weekEnd)}</p>

              <p className="text-xs text-[#757575]">Submission deadline: {formatWeeklyCheckInDate(selectedWeek.graceEnd)}</p>

              {selectedWeek.submission && <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F7F7F7] p-3 text-xs">

                {[ ["Opening cash", "opening_cash_balance"], ["Total sales", "total_sales"], ["Cash received", "cash_sales_received"], ["Total spent", "total_spent"], ["Cash spent", "cash_spent"], ["Closing cash", "closing_cash_balance"] ].map(([name, key]) => <div key={key}>{name}<div className="font-bold">{formatMoney(selectedWeek.submission[key])}</div></div>)}

              </div>}

              {!selectedWeek.submission && canEditWeeklyCheckIn(selectedWeek.week_start) ?

                <button type="button" onClick={() => openCheckIn(selectedWeek)} className="w-full rounded-xl bg-[#FF914D] px-4 py-3 text-sm font-black text-white">COMPLETE CHECK-IN</button> :

                <p className="text-xs text-[#757575]">{selectedWeek.submission ? "This completed check-in is read-only." : selectedWeek.status === "missed" ? "The submission deadline has passed." : "Available from Friday of the reporting week."}</p>}

            </div>}

          </>}

        </section>

      </main>

      <WeeklyFinancialCheckIn open={!!formWeek} weekStart={formWeek || undefined} onClose={() => setFormWeek(null)} onSubmitted={() => setRefreshKey((n) => n + 1)} />

      <BottomBar />

    </div>

  );

}
